import { today } from "@repo/helpers";
import { EditableGridPage } from "@repo/ui";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useRepDisciplineQueries } from "../../api/repDisciplineApi";
import { RepDisciplinesProvider, useRepContext, useRepDisciplineContext } from "../../contexts/editableItemContext";
import { usePermissionsCheck } from "../../helpers/permissions";
import { useEntityId, useUpdateRoute } from "../../helpers/urlHooks";
import { RepDiscipline } from "../../models";
import { RepDisciplineType } from "../../models/repDiscipline";
import RepDisciplineEditorDynamic from "./editor";
import RepDisciplineGridBasic from "./grid";

function Disciplines() {
	return (
		<RepDisciplinesProvider>
			<DisciplinesInternal />
		</RepDisciplinesProvider>
	);
}

function DisciplinesInternal() {
	const { repId } = useRepContext();
	const { overwriteItem, pendingChanges } = useRepDisciplineContext();
	const { useRepDisciplineQuery } = useRepDisciplineQueries();
	const { data, loaded } = useRepDisciplineQuery();
	const location = useLocation();
	const entityId = useEntityId();
	const updateRoute = useUpdateRoute();

	const { fullAPReadOnly } = usePermissionsCheck();

	const openItem = useCallback(
		(item: RepDiscipline) => {
			overwriteItem(item);
		},
		[overwriteItem],
	);

	const newItem = useCallback(
		(action?: string) => {
			const baseItem = {
				startDate: today(),
				representativeId: repId,
			} as RepDiscipline;

			switch (action) {
				case "suspend": {
					baseItem.type = RepDisciplineType.Suspension;
					baseItem.typeId = RepDisciplineType.Suspension;
					baseItem.typeName = "Suspension";
					break;
				}
				case "blocklist": {
					baseItem.type = RepDisciplineType.Blocklist;
					baseItem.typeId = RepDisciplineType.Blocklist;
					baseItem.typeName = "Blocklist";
					break;
				}
				default:
					break;
			}

			overwriteItem(baseItem);
		},
		[overwriteItem, repId],
	);

	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const action = urlParams.get("action");

		if (action) {
			newItem(action);
		}
	}, [location.search, newItem]);

	return (
		<EditableGridPage<RepDiscipline>
			id="RepDisciplineContainer"
			items={data}
			keyProperty="repDisciplineId"
			Editor={RepDisciplineEditorDynamic}
			Grid={RepDisciplineGridBasic}
			openItem={openItem}
			pendingChanges={pendingChanges}
			newItem={newItem}
			itemsLoaded={loaded}
			entityId={entityId}
			updateRoute={updateRoute}
			fullReadOnly={fullAPReadOnly}
		/>
	);
}

export default Disciplines;
